/**
 * Relation profiles — Track R.
 * Classic friends/enemies is the only exit profile (R=2).
 */

export type RelationRole = {
  id: string;
  label: string;
  /** Default signed weight; classic overrides from SimParams each step */
  weight: number;
  colorRgb?: readonly [number, number, number];
};

export type RelationProfile = {
  id: string;
  label: string;
  roles: RelationRole[];
};

export const CLASSIC_FE_PROFILE_ID = "classic_fe";

/** Friend / enemy RGB (0–1) — matches relationColors.ts */
export const CLASSIC_FE_PROFILE: RelationProfile = {
  id: CLASSIC_FE_PROFILE_ID,
  label: "Friends & enemies",
  roles: [
    {
      id: "friend",
      label: "Friend",
      weight: 0.02,
      colorRgb: [0.35, 0.75, 1.0],
    },
    {
      id: "enemy",
      label: "Enemy",
      weight: -0.01,
      colorRgb: [1.0, 0.4, 0.35],
    },
  ],
};

const PROFILES: Record<string, RelationProfile> = {
  [CLASSIC_FE_PROFILE_ID]: CLASSIC_FE_PROFILE,
};

export function getRelationProfile(
  id: string | undefined | null,
): RelationProfile {
  const key = id ?? CLASSIC_FE_PROFILE_ID;
  return PROFILES[key] ?? CLASSIC_FE_PROFILE;
}

export function listRelationProfiles(): RelationProfile[] {
  return Object.values(PROFILES);
}

export function isClassicFeDispatch(
  relationProfile: string | undefined | null,
): boolean {
  return (relationProfile ?? CLASSIC_FE_PROFILE_ID) === CLASSIC_FE_PROFILE_ID;
}

/**
 * Resolve signed role weights for this step.
 * classic_fe: friendStep / −enemyStep are source of truth (choreography-safe).
 */
export function resolveRoleWeights(
  params: {
    friendStep: number;
    enemyStep: number;
    relationProfile?: string;
    relationWeights?: number[] | null;
  },
  profile?: RelationProfile,
): Float64Array {
  const prof = profile ?? getRelationProfile(params.relationProfile);
  const R = prof.roles.length;
  const out = new Float64Array(R);
  if (isClassicFeDispatch(params.relationProfile)) {
    out[0] = params.friendStep;
    if (R >= 2) out[1] = -params.enemyStep;
    return out;
  }
  const side = params.relationWeights;
  for (let r = 0; r < R; r++) {
    out[r] = side && side.length === R ? side[r]! : prof.roles[r]!.weight;
  }
  return out;
}

/** Planar pack: role-major targets[r*n + i]. */
export function packPlanar(
  friends: Int32Array,
  enemies: Int32Array,
): Int32Array {
  const n = friends.length;
  if (enemies.length !== n) {
    throw new Error(
      `packPlanar: length mismatch ${friends.length} vs ${enemies.length}`,
    );
  }
  const targets = new Int32Array(n * 2);
  targets.set(friends, 0);
  targets.set(enemies, n);
  return targets;
}

export function rolePlane(
  targets: Int32Array,
  n: number,
  r: number,
): Int32Array {
  return targets.subarray(r * n, r * n + n);
}

export function allocTargets(n: number, R: number): Int32Array {
  return new Int32Array(n * R);
}

/** Copy planar planes from n0 → n1 buffer (n1 >= n0). */
export function copyPlanes(
  oldT: Int32Array,
  newT: Int32Array,
  n0: number,
  n1: number,
  R: number,
): void {
  for (let r = 0; r < R; r++) {
    newT.set(oldT.subarray(r * n0, r * n0 + n0), r * n1);
  }
}
